import React, {useEffect, useState} from "react";
import {
    Button,
    Card,
    Col,
    ColorPicker,
    Divider,
    Empty,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    Tooltip,
    Typography,
    message,
} from "antd";
import {Upload, Tabs, Badge, Alert, Affix, Grid} from "antd";
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    CopyOutlined,
    DeleteOutlined,
    ExclamationCircleFilled,
    PlusOutlined,
    SaveOutlined,
    UploadOutlined,
} from "@ant-design/icons";
import {useWidgetSettings, useUpdateWidgetSettings} from "./widget-settings-query";
import WidgetPreview from "./widget-preview";
import TranslationsTab from "./translations-tab";

const {Title, Text, Paragraph} = Typography;
const {TextArea} = Input;

// Field types the pre-chat form builder offers — mirrors the backend PreChatFieldType enum.
const FIELD_TYPES = [
    {value: "TEXT", label: "متن کوتاه"},
    {value: "TEXTAREA", label: "متن بلند"},
    {value: "EMAIL", label: "ایمیل"},
    {value: "PHONE", label: "شماره تماس"},
    {value: "NUMBER", label: "عدد"},
    {value: "SELECT", label: "انتخابی (لیست)"},
];

const POSITIONS = [
    {value: "BOTTOM_LEFT", label: "پایین-چپ"},
    {value: "BOTTOM_RIGHT", label: "پایین-راست"},
];

const hexOf = (v) => (typeof v === "string" ? v : v?.toHexString?.() || "#e1b137");

// Logos are stored inline as data URIs (no MinIO object, no expiring presigned URL, no CORS),
// so the whole "upload" happens in the browser: read the file, hand the data URI to the form.
const MAX_LOGO_BYTES = 700 * 1024;

// The proactive invitation: same copy the widget falls back to when the field is left empty,
// shown here only as a placeholder so the admin sees what "empty" actually means.
const DEFAULT_PROACTIVE_MESSAGE =
    "سلام 👋 اگر سوالی دارید، کارشناسان ما همین حالا آنلاین‌اند و آماده‌ی کمک هستند.";

// The server clamps the delay to this range on save; mirroring it here keeps the admin from
// saving a number that quietly comes back different.
const PROACTIVE_DELAY_MIN = 5;
const PROACTIVE_DELAY_MAX = 600;
const PROACTIVE_DELAY_DEFAULT = 30;

const clampProactiveDelay = (n) => {
    const seconds = Math.round(Number(n));
    if (!Number.isFinite(seconds)) return PROACTIVE_DELAY_DEFAULT;
    return Math.min(PROACTIVE_DELAY_MAX, Math.max(PROACTIVE_DELAY_MIN, seconds));
};

/**
 * دو تغییر در این صفحه روی محیط عملیاتی برگشت‌ناپذیرِ آنی دارند و تا امروز بدون هیچ پرسشی
 * ذخیره می‌شدند:
 *   ۱) خاموش کردن `enabled` → ویجت روی کل سایت کارگزاری بالا نمی‌آید.
 *   ۲) حذف یک دامنه از `allowedOrigins` → ویجت روی آن دامنه بی‌صدا بوت نمی‌شود.
 * این تابع، وضعیت ذخیره‌شده‌ی سرور را با payload مقایسه می‌کند تا فقط همین دو گذارِ خطرناک
 * را تشخیص دهد (روشن کردن ویجت یا افزودن دامنه، تأیید نمی‌خواهد).
 */
const describeDestructiveChanges = (saved, payload) => {
    const wasEnabled = saved?.enabled ?? true;
    const disabling = wasEnabled === true && payload.enabled === false;

    const savedOrigins = (saved?.allowedOrigins || [])
        .map((o) => (o || "").trim())
        .filter(Boolean);
    const nextOrigins = payload.allowedOrigins || [];
    const removedOrigins = savedOrigins.filter((o) => !nextOrigins.includes(o));

    return {disabling, removedOrigins};
};

const confirmDestructiveChanges = ({disabling, removedOrigins}) =>
    new Promise((resolve) => {
        Modal.confirm({
            title: "این تغییر روی سایت زنده اثر فوری دارد",
            icon: <ExclamationCircleFilled style={{color: "#ff4d4f"}}/>,
            width: 560,
            okText: "بله، ذخیره کن",
            okButtonProps: {danger: true},
            cancelText: "انصراف",
            content: (
                <Space direction="vertical" size={10} style={{width: "100%", marginTop: 8}}>
                    {disabling && (
                        <Alert
                            type="error"
                            showIcon
                            message="ویجت گفت‌وگو خاموش می‌شود"
                            description="با ذخیره‌ی این تغییر، ویجت روی هیچ صفحه‌ای از سایت بارگذاری نمی‌شود و مشتریان تا روشن شدن دوباره، هیچ راهی برای شروع گفت‌وگو ندارند."
                        />
                    )}
                    {removedOrigins.length > 0 && (
                        <Alert
                            type="error"
                            showIcon
                            message="این دامنه‌ها از فهرست مجاز حذف می‌شوند"
                            description={
                                <>
                                    <div>ویجت دیگر روی این دامنه‌ها بالا نمی‌آید (بدون هیچ پیام خطایی برای بازدیدکننده):</div>
                                    <ul style={{margin: "6px 0 0", paddingInlineStart: 18}}>
                                        {removedOrigins.map((o) => (
                                            <li key={o} dir="ltr"
                                                style={{fontFamily: "monospace", fontSize: 12}}>
                                                {o}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            }
                        />
                    )}
                </Space>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });

const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

/**
 * A single logo slot: preview + upload + clear. Value is a data URI string held in the AntD form,
 * so it round-trips through the normal save like any other field.
 */
const LogoField = ({value, onChange, hint, previewBg}) => {
    const beforeUpload = async (file) => {
        if (!file.type?.startsWith("image/")) {
            message.error("فقط فایل تصویری مجاز است");
            return Upload.LIST_IGNORE;
        }
        if (file.size > MAX_LOGO_BYTES) {
            message.error("حجم تصویر باید کمتر از ۷۰۰ کیلوبایت باشد");
            return Upload.LIST_IGNORE;
        }
        try {
            onChange?.(await readFileAsDataUrl(file));
            message.success("تصویر انتخاب شد — برای ثبت، «ذخیره تغییرات» را بزنید");
        } catch {
            message.error("خواندن فایل ناموفق بود");
        }
        return Upload.LIST_IGNORE; // we never POST it ourselves; it saves with the form
    };

    return (
        <Space direction="vertical" size={8} style={{width: "100%"}}>
            <div
                style={{
                    height: 76,
                    border: "1px dashed var(--npe-border, #d9d9d9)",
                    borderRadius: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: previewBg || "transparent",
                    padding: 8,
                }}
            >
                {value ? (
                    <img src={value} alt="logo" style={{maxHeight: 60, maxWidth: "100%", objectFit: "contain"}}/>
                ) : (
                    <Text type="secondary" style={{fontSize: 12}}>تصویری انتخاب نشده</Text>
                )}
            </div>
            <Space>
                <Upload beforeUpload={beforeUpload} showUploadList={false} accept="image/*">
                    <Button size="small" icon={<UploadOutlined/>}>انتخاب تصویر</Button>
                </Upload>
                {value && (
                    <Button size="small" danger type="text" icon={<DeleteOutlined/>} onClick={() => onChange?.("")}>
                        حذف
                    </Button>
                )}
            </Space>
            {hint && <Text type="secondary" style={{fontSize: 11}}>{hint}</Text>}
        </Space>
    );
};

const WidgetSettings = () => {
    const screens = Grid.useBreakpoint();
    const isWide = !!screens.md;
    const [form] = Form.useForm();
    const {data, isLoading, isError} = useWidgetSettings();
    const {mutateAsync: save, isPending: saving} = useUpdateWidgetSettings();
    const [siteKey, setSiteKey] = useState("");
    const [installSnippet, setInstallSnippet] = useState("");
    const [dirty, setDirty] = useState(false);
    const [activeTab, setActiveTab] = useState("general");

    // Watch the field list so a SELECT field reveals its options editor reactively.
    const preChatFields = Form.useWatch("preChatFields", form) || [];
    const watched = Form.useWatch([], form) || {};
    // ColorPicker hands back an object; the preview wants a plain hex.
    const previewValues = {
        ...watched,
        brandColor: hexOf(watched.brandColor),
    };

    useEffect(() => {
        if (!data) return;
        setSiteKey(data.siteKey || "");
        setInstallSnippet(data.installSnippet || "");
        form.setFieldsValue({
            enabled: data.enabled ?? true,
            brandName: data.brandName || "",
            secondaryColor: data.secondaryColor || "#1a1a1a",
            widgetLogo: data.widgetLogo || "",
            consoleLogo: data.consoleLogo || "",
            brandColor: data.brandColor || "#e1b137",
            position: data.position || "BOTTOM_LEFT",
            title: data.title || "",
            subtitle: data.subtitle || "",
            greeting: data.greeting || "",
            offlineMessage: data.offlineMessage || "",
            privacyNotice: data.privacyNotice || "",
            proactiveEnabled: data.proactiveEnabled ?? true,
            proactiveDelaySeconds: data.proactiveDelaySeconds ?? PROACTIVE_DELAY_DEFAULT,
            proactiveMessage: data.proactiveMessage || "",
            preChatEnabled: data.preChatEnabled ?? true,
            agentFirst: data.agentFirst ?? true,
            escalateAfterUnresolvedTurns: data.escalateAfterUnresolvedTurns ?? null,
            verifyEmailWithBroker: data.verifyEmailWithBroker ?? false,
            allowFileUpload: data.allowFileUpload ?? false,
            maxConversationsPerIpPerHour: data.maxConversationsPerIpPerHour ?? null,
            maxMessagesPerVisitorPerMinute: data.maxMessagesPerVisitorPerMinute ?? null,
            allowedOrigins: (data.allowedOrigins && data.allowedOrigins.length ? data.allowedOrigins : [""]),
            hiddenPaths: data.hiddenPaths || [],
            translations: data.translations || {},
            preChatFields: (data.preChatFields || []).map((f) => ({
                key: f.key,
                label: f.label,
                type: f.type,
                required: !!f.required,
                placeholder: f.placeholder || "",
                options: f.options || [],
                i18n: f.i18n || {},
            })),
        });
    }, [data, form]);

    const onSave = async () => {
        try {
            const v = await form.validateFields();
            // validateFields() returns only the fields that are mounted, and the translation boxes
            // are mounted for ONE language at a time (the one picked on the translations tab). The
            // server replaces translations wholesale, so saving with «العربية» picked deleted every
            // English translation, and vice versa. The form store holds every language loaded from
            // the server plus the admin's edits, so translations are read from there instead.
            const all = form.getFieldsValue(true);
            const payload = {
                ...v,
                brandColor: hexOf(v.brandColor),
                secondaryColor: hexOf(v.secondaryColor),
                proactiveEnabled: v.proactiveEnabled !== false,
                proactiveDelaySeconds: clampProactiveDelay(v.proactiveDelaySeconds),
                // خالی بودن این فیلد معنادار است: ویجت به متن پیش‌فرض خودش برمی‌گردد.
                proactiveMessage: (v.proactiveMessage || "").trim() || null,
                // Drop blank origin rows the admin left empty.
                allowedOrigins: (v.allowedOrigins || []).map((o) => (o || "").trim()).filter(Boolean),
                hiddenPaths: (v.hiddenPaths || []).map((p) => (p || "").trim()).filter(Boolean),
                translations: all.translations || v.translations || {},
                preChatFields: (v.preChatFields || []).map((f, i) => ({
                    i18n: all.preChatFields?.[i]?.i18n || f.i18n || {},
                    key: (f.key || "").trim(),
                    label: (f.label || "").trim(),
                    type: f.type,
                    required: !!f.required,
                    displayOrder: i + 1,
                    placeholder: f.placeholder || null,
                    options: f.type === "SELECT" ? (f.options || []) : [],
                })),
            };

            // خاموش کردن ویجت یا حذف دامنه‌ی مجاز، هر دو سرویسِ مشتریان را قطع می‌کنند؛
            // پیش از ارسال به سرور، تأیید صریح می‌گیریم.
            const destructive = describeDestructiveChanges(data, payload);
            if (destructive.disabling || destructive.removedOrigins.length > 0) {
                const confirmed = await confirmDestructiveChanges(destructive);
                if (!confirmed) return;
            }

            const fresh = await save(payload);
            if (fresh?.installSnippet) setInstallSnippet(fresh.installSnippet);
            if (fresh?.siteKey) setSiteKey(fresh.siteKey);
            setDirty(false);
            message.success("تنظیمات ویجت با موفقیت ذخیره شد");
        } catch (e) {
            if (!e?.errorFields) {
                message.error(e?.response?.data?.message || "ذخیره‌سازی ناموفق بود");
            } else {
                message.warning("لطفاً خطاهای فرم را برطرف کنید");
            }
        }
    };

    const copySnippet = async () => {
        try {
            await navigator.clipboard.writeText(installSnippet);
            message.success("کد نصب کپی شد");
        } catch {
            message.error("کپی ناموفق بود");
        }
    };

    if (isLoading) {
        return (
            <div style={{textAlign: "center", padding: 80}}>
                <Spin size="large"/>
            </div>
        );
    }
    if (isError) {
        return <Empty description="بارگذاری تنظیمات ویجت ناموفق بود"/>;
    }

    /* Each tab is just a titled group of the existing cards; the form stays a single form so
       validation and save behave exactly as before regardless of which tab is open. */
    const tabItems = [
        {key: "general", label: "عمومی و نصب"},
        {key: "brand", label: "برند و ظاهر"},
        {key: "content", label: "متن‌ها"},
        {key: "form", label: "فرم اطلاعات اولیه"},
        {key: "behaviour", label: "رفتار و مسیریابی"},
        {key: "security", label: "امنیت و محدودیت‌ها"},
        {key: "i18n", label: "زبان‌ها و ترجمه"},
    ];

    // Only the active tab's sections are shown. They stay mounted inside the same <Form>, so a
    // validation error on a hidden tab still blocks save — we just have to point the admin at it.
    const show = (key) => ({display: activeTab === key ? "block" : "none"});

    return (
        <div style={{maxWidth: 1180, margin: "0 auto", paddingBottom: 8}}>
            <Row justify="space-between" align="middle" style={{marginBottom: 4}}>
                <Col>
                    <Title level={3} style={{margin: 0}}>تنظیمات ویجت چت آنلاین</Title>
                    <Text type="secondary">پیکربندی ویجتی که روی وب‌سایت شرکت نمایش داده می‌شود</Text>
                </Col>
            </Row>

            {/* وضعیت واقعیِ ذخیره‌شده روی سرور — نه مقدار فعلی فرم — تا خاموش ماندن ویجت
                در هیچ تبی از دید ادمین پنهان نماند. */}
            {data?.enabled === false && (
                <Alert
                    type="error"
                    showIcon
                    style={{marginTop: 12}}
                    message="ویجت گفت‌وگو هم‌اکنون غیرفعال است"
                    description="ویجت روی هیچ صفحه‌ای از سایت بارگذاری نمی‌شود و مشتریان نمی‌توانند گفت‌وگوی تازه‌ای شروع کنند. برای بازگرداندن، در تب «عمومی و نصب» گزینه‌ی «نمایش ویجت روی سایت» را روشن و ذخیره کنید."
                />
            )}

            {/* Save action and tab strip share one row on a wide screen and stack on a narrow one.
                Side by side on a phone the save button took more than a third of the width and left
                the seven tabs 207px to share — four of them ended up off the edge of the screen with
                no way to scroll to them, so behaviour, security, translations and the pre-chat form
                could not be opened at all from a phone. Stacked, the strip gets the full width. */}
            <Affix offsetTop={0}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: isWide ? "row" : "column",
                        alignItems: isWide ? "center" : "stretch",
                        justifyContent: "space-between",
                        gap: isWide ? 12 : 4,
                        padding: "10px 0",
                        background: "var(--npe-canvas)",
                        borderBottom: "1px solid var(--npe-hairline)",
                        marginBottom: 16,
                    }}
                >
                    {!isWide && (
                        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8}}>
                            {dirty
                                ? <Badge status="warning" text={<Text type="secondary" style={{fontSize: 12}}>ذخیره‌نشده</Text>}/>
                                : <span/>}
                            <Button type="primary" icon={<SaveOutlined/>} loading={saving} onClick={onSave}>
                                ذخیره تغییرات
                            </Button>
                        </div>
                    )}

                    <Tabs
                        activeKey={activeTab}
                        onChange={setActiveTab}
                        items={tabItems}
                        // min-width:0 is what lets a flex child be narrower than its own content;
                        // without it the strip measures itself at its full 787px, AntD sees no
                        // overflow, and neither its scroll arrows nor a "more" menu ever appear.
                        style={{flex: 1, minWidth: 0, marginBottom: -16}}
                    />

                    {isWide && (
                        <Space>
                            {dirty && <Badge status="warning" text={<Text type="secondary" style={{fontSize: 12}}>ذخیره‌نشده</Text>}/>}
                            <Button type="primary" icon={<SaveOutlined/>} loading={saving} onClick={onSave}>
                                ذخیره تغییرات
                            </Button>
                        </Space>
                    )}
                </div>
            </Affix>

            <Row gutter={20}>
                <Col xs={24} lg={16}>

            {/* ==================== GENERAL ==================== */}
            <div style={show("general")}>
            {/* Install snippet */}
            <Card size="small" style={{marginBottom: 16}}>
                <Text strong>کد نصب</Text>
                <Paragraph type="secondary" style={{fontSize: 12, margin: "4px 0 8px"}}>
                    این یک خط را در انتهای صفحات وب‌سایت خود قرار دهید. برای فعال شدن، دامنه‌ی سایت باید در
                    فهرست «دامنه‌های مجاز» پایین اضافه شده باشد.
                </Paragraph>
                <Space.Compact style={{width: "100%"}}>
                    <Input readOnly value={installSnippet} dir="ltr" style={{fontFamily: "monospace", fontSize: 12}}/>
                    <Tooltip title="کپی">
                        <Button icon={<CopyOutlined/>} onClick={copySnippet}/>
                    </Tooltip>
                </Space.Compact>
                <div style={{marginTop: 8}}>
                    <Text type="secondary" style={{fontSize: 12}}>Site Key: </Text>
                    <Text code copyable>{siteKey}</Text>
                </div>
            </Card>
            </div>

            <Form form={form} layout="vertical" requiredMark="optional" onValuesChange={() => setDirty(true)}>
                {/* ---- GENERAL (cont.) ---- */}
                <div style={show("general")}>
                <Card size="small" title="وضعیت" style={{marginBottom: 16}}>
                    <Form.Item
                        name="enabled"
                        valuePropName="checked"
                        label="نمایش ویجت روی سایت"
                        tooltip="با خاموش کردن این گزینه، ویجت روی هیچ صفحه‌ای بارگذاری نمی‌شود"
                        style={{marginBottom: 0}}
                    >
                        <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال"/>
                    </Form.Item>
                </Card>

                {/* Lives next to the on/off switch because it answers the same question — where
                    does this thing appear — one page at a time instead of all or nothing. */}
                <Card size="small" title="صفحه‌هایی که ویجت در آن‌ها نمایش داده نشود" style={{marginBottom: 16}}>
                    <Paragraph type="secondary" style={{fontSize: 12, marginBottom: 12}}>
                        اگر این فهرست خالی باشد، ویجت روی همه‌ی صفحه‌های سایت دیده می‌شود. هر آدرسی که
                        اینجا اضافه کنید، ویجت در آن صفحه اصلاً بارگذاری نمی‌شود — نه حباب گفت‌وگو، نه
                        دعوت خودکار، و نه ثبت مسیر بازدید.
                        <br/>
                        با <Text code dir="ltr">*</Text> می‌توانید چند صفحه را یکجا مشخص کنید:
                        <Text code dir="ltr">/checkout</Text> فقط همان صفحه،
                        <Text code dir="ltr">/account/*</Text> همه‌ی صفحه‌های زیرمجموعه، و
                        <Text code dir="ltr">*/admin/*</Text> هر آدرسی که این بخش را داشته باشد.
                    </Paragraph>
                    <Form.List name="hiddenPaths">
                        {(fields, {add, remove}) => (
                            <>
                                {fields.map((field) => (
                                    <Space key={field.key} style={{display: "flex", marginBottom: 8}} align="baseline">
                                        <Form.Item
                                            {...field}
                                            style={{marginBottom: 0, width: 520, maxWidth: "70vw"}}
                                        >
                                            <Input dir="ltr" placeholder="/checkout/*"/>
                                        </Form.Item>
                                        <Button danger type="text" icon={<DeleteOutlined/>} onClick={() => remove(field.name)}/>
                                    </Space>
                                ))}
                                <Button type="dashed" onClick={() => add("")} icon={<PlusOutlined/>}>
                                    افزودن آدرس
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                </div>

                {/* ==================== BRAND ==================== */}
                <div style={show("brand")}>
                <Card size="small" title="برند و هویت بصری" style={{marginBottom: 16}}>
                    <Paragraph type="secondary" style={{fontSize: 12}}>
                        این تنظیمات هم روی ویجت سایت و هم روی پنل ادمین/اپراتور (سربرگ و صفحه‌ی ورود) اعمال می‌شود.
                    </Paragraph>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item name="brandName" label="نام برند" rules={[{max: 255}]}>
                                <Input placeholder="مثلاً NPE"/>
                            </Form.Item>
                        </Col>
                        <Col xs={12} sm={8}>
                            <Form.Item name="brandColor" label="رنگ اصلی">
                                <ColorPicker format="hex" showText/>
                            </Form.Item>
                        </Col>
                        <Col xs={12} sm={8}>
                            <Form.Item name="secondaryColor" label="رنگ ثانویه">
                                <ColorPicker format="hex" showText/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="widgetLogo" label="لوگوی ویجت">
                                <LogoField hint="در سربرگ ویجت روی سایت نمایش داده می‌شود"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="consoleLogo" label="لوگوی پنل">
                                <LogoField hint="در سربرگ پنل و صفحه‌ی ورود نمایش داده می‌شود"/>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Appearance */}
                <Card size="small" title="ظاهر ویجت" style={{marginBottom: 16}}>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item name="position" label="موقعیت حباب">
                                <Select options={POSITIONS}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="title" label="عنوان" rules={[{max: 255}]}>
                                <Input placeholder="مثلاً پشتیبانی NPE"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="subtitle" label="زیرعنوان" rules={[{max: 255}]}>
                                <Input placeholder="مثلاً معمولاً در چند دقیقه پاسخ می‌دهیم"/>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                </div>

                {/* ==================== CONTENT ==================== */}
                <div style={show("content")}>
                <Card size="small" title="متن‌ها" style={{marginBottom: 16}}>
                    <Form.Item name="greeting" label="پیام خوش‌آمد">
                        <TextArea rows={2} placeholder="اولین پیامی که کاربر می‌بیند"/>
                    </Form.Item>
                    <Form.Item name="offlineMessage" label="پیام خارج از ساعت کاری">
                        <TextArea rows={2} placeholder="وقتی هیچ اپراتوری آنلاین نیست نمایش داده می‌شود"/>
                    </Form.Item>
                    <Form.Item name="privacyNotice" label="متن حریم خصوصی" style={{marginBottom: 0}}>
                        <TextArea rows={2} placeholder="زیر فرم اولیه نمایش داده می‌شود"/>
                    </Form.Item>
                </Card>

                {/* دعوت خودکار — تنها پیامی است که ویجت بدون درخواست بازدیدکننده نشان می‌دهد،
                    پس شرط‌هایش را همین‌جا صریح می‌نویسیم تا انتظارِ ادمین با رفتار واقعی یکی باشد. */}
                <Card
                    size="small"
                    title="دعوت خودکار به گفت‌وگو"
                    style={{marginBottom: 16}}
                    extra={
                        <Form.Item name="proactiveEnabled" valuePropName="checked" noStyle>
                            <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال"/>
                        </Form.Item>
                    }
                >
                    <Paragraph type="secondary" style={{fontSize: 12}}>
                        اگر بازدیدکننده هنوز گفت‌وگویی شروع نکرده باشد، پس از زمان تعیین‌شده یک کارت کوچک
                        کنار حباب ویجت ظاهر می‌شود. پنل گفت‌وگو خودبه‌خود باز نمی‌شود و هیچ صدایی پخش نمی‌شود؛
                        با کلیک روی کارت گفت‌وگو باز می‌شود و با × بسته می‌شود. برای هر بازدیدکننده فقط
                        یک‌بار نمایش داده می‌شود و در بازدیدهای بعدی تکرار نمی‌شود.
                    </Paragraph>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="proactiveDelaySeconds"
                                label="تأخیر نمایش (ثانیه)"
                                tooltip="از لحظه‌ی بارگذاری صفحه شمرده می‌شود، نه از آخرین فعالیت کاربر"
                                extra="بین ۵ تا ۶۰۰ ثانیه"
                                rules={[{
                                    type: "number",
                                    min: PROACTIVE_DELAY_MIN,
                                    max: PROACTIVE_DELAY_MAX,
                                    message: "عددی بین ۵ تا ۶۰۰ ثانیه وارد کنید",
                                }]}
                            >
                                <InputNumber
                                    min={PROACTIVE_DELAY_MIN}
                                    max={PROACTIVE_DELAY_MAX}
                                    style={{width: "100%"}}
                                    placeholder="پیش‌فرض ۳۰"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        name="proactiveMessage"
                        label="متن دعوت"
                        extra="خالی بگذارید تا متن پیش‌فرض خودِ ویجت نمایش داده شود."
                        style={{marginBottom: 0}}
                    >
                        <TextArea rows={2} placeholder={DEFAULT_PROACTIVE_MESSAGE}/>
                    </Form.Item>
                </Card>

                </div>

                {/* ==================== SECURITY (origins) ==================== */}
                <div style={show("security")}>
                <Card size="small" title="دامنه‌های مجاز" style={{marginBottom: 16}}>
                    <Paragraph type="secondary" style={{fontSize: 12}}>
                        فقط سایت‌هایی که دامنه‌شان اینجا ثبت شده اجازه‌ی استفاده از ویجت را دارند. با آدرس کامل
                        وارد کنید، مثل <Text code dir="ltr">https://npemarket.com</Text>
                    </Paragraph>
                    <Form.List name="allowedOrigins">
                        {(fields, {add, remove}) => (
                            <>
                                {fields.map((field) => (
                                    <Space key={field.key} style={{display: "flex", marginBottom: 8}} align="baseline">
                                        <Form.Item
                                            {...field}
                                            style={{marginBottom: 0, width: 520, maxWidth: "70vw"}}
                                            rules={[{
                                                pattern: /^https?:\/\/.+/i,
                                                message: "باید با http:// یا https:// شروع شود",
                                            }]}
                                        >
                                            <Input dir="ltr" placeholder="https://example.com"/>
                                        </Form.Item>
                                        <Button danger type="text" icon={<DeleteOutlined/>} onClick={() => remove(field.name)}/>
                                    </Space>
                                ))}
                                <Button type="dashed" onClick={() => add("")} icon={<PlusOutlined/>}>
                                    افزودن دامنه
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                </div>

                {/* ==================== PRE-CHAT FORM ==================== */}
                <div style={show("form")}>
                <Card
                    size="small"
                    title="فرم اطلاعات اولیه"
                    style={{marginBottom: 16}}
                    extra={
                        <Form.Item name="preChatEnabled" valuePropName="checked" noStyle>
                            <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال"/>
                        </Form.Item>
                    }
                >
                    <Paragraph type="secondary" style={{fontSize: 12}}>
                        فیلدهایی که کاربر پیش از شروع گفت‌وگو پر می‌کند. ترتیب نمایش همان ترتیب این فهرست است.
                    </Paragraph>
                    <Form.List name="preChatFields">
                        {(fields, {add, remove, move}) => (
                            <>
                                {fields.length === 0 && (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="هیچ فیلدی تعریف نشده"
                                        style={{margin: "12px 0"}}
                                    />
                                )}
                                {fields.map((field, index) => {
                                    const type = preChatFields?.[index]?.type;
                                    return (
                                        <Card
                                            key={field.key}
                                            size="small"
                                            style={{marginBottom: 10}}
                                            bodyStyle={{padding: 12}}
                                        >
                                            <Row gutter={8} align="middle">
                                                <Col xs={24} sm={6}>
                                                    <Form.Item
                                                        {...field}
                                                        key="key"
                                                        name={[field.name, "key"]}
                                                        label="شناسه"
                                                        style={{marginBottom: 8}}
                                                        rules={[
                                                            {required: true, message: "الزامی"},
                                                            {pattern: /^[A-Za-z][A-Za-z0-9_]*$/, message: "فقط حروف/عدد/آندرلاین"},
                                                        ]}
                                                    >
                                                        <Input dir="ltr" placeholder="email"/>
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={7}>
                                                    <Form.Item
                                                        name={[field.name, "label"]}
                                                        label="عنوان فیلد"
                                                        style={{marginBottom: 8}}
                                                        rules={[{required: true, message: "الزامی"}]}
                                                    >
                                                        <Input placeholder="ایمیل"/>
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={6}>
                                                    <Form.Item
                                                        name={[field.name, "type"]}
                                                        label="نوع"
                                                        style={{marginBottom: 8}}
                                                        rules={[{required: true, message: "الزامی"}]}
                                                    >
                                                        <Select options={FIELD_TYPES}/>
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={12} sm={3}>
                                                    <Form.Item
                                                        name={[field.name, "required"]}
                                                        label="اجباری"
                                                        valuePropName="checked"
                                                        style={{marginBottom: 8}}
                                                    >
                                                        <Switch size="small"/>
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={12} sm={2} style={{textAlign: "left"}}>
                                                    <Space size={0}>
                                                        <Tooltip title="بالا">
                                                            <Button type="text" size="small" icon={<ArrowUpOutlined/>}
                                                                    disabled={index === 0}
                                                                    onClick={() => move(index, index - 1)}/>
                                                        </Tooltip>
                                                        <Tooltip title="پایین">
                                                            <Button type="text" size="small" icon={<ArrowDownOutlined/>}
                                                                    disabled={index === fields.length - 1}
                                                                    onClick={() => move(index, index + 1)}/>
                                                        </Tooltip>
                                                    </Space>
                                                </Col>
                                            </Row>
                                            <Row gutter={8}>
                                                <Col xs={24} sm={type === "SELECT" ? 10 : 22}>
                                                    <Form.Item
                                                        name={[field.name, "placeholder"]}
                                                        label="راهنما (placeholder)"
                                                        style={{marginBottom: 0}}
                                                    >
                                                        <Input placeholder="اختیاری"/>
                                                    </Form.Item>
                                                </Col>
                                                {type === "SELECT" && (
                                                    <Col xs={24} sm={12}>
                                                        <Form.Item
                                                            name={[field.name, "options"]}
                                                            label="گزینه‌ها"
                                                            style={{marginBottom: 0}}
                                                            rules={[{
                                                                validator: (_, val) =>
                                                                    val && val.length
                                                                        ? Promise.resolve()
                                                                        : Promise.reject(new Error("حداقل یک گزینه لازم است")),
                                                            }]}
                                                        >
                                                            <Select mode="tags" tokenSeparators={[",", "،"]}
                                                                    placeholder="تایپ کنید و Enter بزنید"/>
                                                        </Form.Item>
                                                    </Col>
                                                )}
                                                <Col xs={24} sm={2} style={{textAlign: "left", alignSelf: "flex-end"}}>
                                                    <Button danger type="text" icon={<DeleteOutlined/>}
                                                            onClick={() => remove(field.name)}/>
                                                </Col>
                                            </Row>
                                        </Card>
                                    );
                                })}
                                <Button
                                    type="dashed"
                                    icon={<PlusOutlined/>}
                                    onClick={() => add({type: "TEXT", required: false, options: []})}
                                >
                                    افزودن فیلد
                                </Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                </div>

                {/* ==================== BEHAVIOUR ==================== */}
                <div style={show("behaviour")}>
                <Card size="small" title="رفتار گفت‌وگو" style={{marginBottom: 16}}>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="agentFirst"
                                valuePropName="checked"
                                label="ابتدا دستیار هوشمند"
                                tooltip="اول AI پاسخ می‌دهد و در صورت نیاز به اپراتور منتقل می‌شود"
                            >
                                <Switch checkedChildren="بله" unCheckedChildren="خیر"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="escalateAfterUnresolvedTurns"
                                label="انتقال بعد از این تعداد پاسخ ناموفق"
                                tooltip="خالی بگذارید تا فقط بر اساس امتیاز شباهت تصمیم گرفته شود"
                            >
                                <InputNumber min={1} max={20} style={{width: "100%"}} placeholder="مثلاً ۳"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="verifyEmailWithBroker"
                                valuePropName="checked"
                                label="تأیید ایمیل با سامانه‌ی کارگزاری"
                            >
                                <Switch checkedChildren="بله" unCheckedChildren="خیر"/>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                </div>

                {/* ==================== SECURITY (limits) ==================== */}
                <div style={show("security")}>
                <Card size="small" title="کنترل سوءاستفاده" style={{marginBottom: 16}}>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item name="maxConversationsPerIpPerHour" label="حداکثر گفت‌وگو در ساعت (هر IP)">
                                <InputNumber min={1} style={{width: "100%"}} placeholder="پیش‌فرض ۵"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="maxMessagesPerVisitorPerMinute" label="حداکثر پیام در دقیقه (هر کاربر)">
                                <InputNumber min={1} style={{width: "100%"}} placeholder="پیش‌فرض ۲۰"/>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="allowFileUpload"
                                valuePropName="checked"
                                label="اجازه‌ی ارسال فایل"
                                tooltip="به کاربران ناشناس اجازه‌ی آپلود فایل می‌دهد — با احتیاط فعال کنید"
                            >
                                <Switch checkedChildren="بله" unCheckedChildren="خیر"/>
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                </div>

                {/* ==================== TRANSLATIONS ==================== */}
                <div style={show("i18n")}>
                    <TranslationsTab form={form} baseValues={watched}/>
                </div>
            </Form>
                </Col>

                {/* Live preview — sticky so it stays visible while the admin edits. */}
                <Col xs={0} lg={8}>
                    <Affix offsetTop={78}>
                        <div>
                            <Text strong style={{display: "block", marginBottom: 10}}>پیش‌نمایش زنده</Text>
                            <WidgetPreview values={previewValues} logo={previewValues.widgetLogo}/>
                            <Alert
                                type="info"
                                showIcon
                                style={{marginTop: 14, fontSize: 12}}
                                message="این پیش‌نمایش تغییرات ذخیره‌نشده را هم نشان می‌دهد."
                            />
                        </div>
                    </Affix>
                </Col>
            </Row>
        </div>
    );
};

export default WidgetSettings;
